/**
 * 单日期 + 时间选择器 — 替换原生 <input type="datetime-local">，复用日历视觉词汇
 * （Mon-first 月历 + 今日点 + 选中态 var(--accent)）+ 时/分 ±步进 + 快捷时间预设。
 * 由 design/Novel-admin/src/datetimepicker.jsx 移植：JSX / className / inline style 原样保留，
 * 仅做 JS→TS（加严格类型）+ window 全局引用（React/ReactDOM/Icons）→ ESM import 的机械转换。
 *
 * value/onChange 走 datetime-local 字符串格式 "YYYY-MM-DDTHH:MM"（空串 = 未设），
 * 故可作为原生 input 的 drop-in 替换。
 * 导出 DateTimeButton（触发按钮 + portal 浮层）与 DateTimePanel（纯面板）。
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icons } from '~/lib/icons';
import { useOwnedFocusScopePortalRef } from './focus-scope';

const DOW = ['一', '二', '三', '四', '五', '六', '日'];
const MONTHS = [
  '1月',
  '2月',
  '3月',
  '4月',
  '5月',
  '6月',
  '7月',
  '8月',
  '9月',
  '10月',
  '11月',
  '12月',
];

function sameDay(a: Date | null | undefined, b: Date | null | undefined): boolean {
  return (
    !!a &&
    !!b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function pad(n: number): string {
  return String(n).padStart(2, '0');
}

// "YYYY-MM-DDTHH:MM" → Date（local），或 null
function parseDT(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(s);
  if (!m) return null;
  // noUncheckedIndexedAccess 下捕获组为 string|undefined，Number(undefined)=NaN 但此处正则已保证全捕获
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
}
// Date → "YYYY-MM-DDTHH:MM"
function fmtDT(d: Date | null | undefined): string {
  if (!d) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
// Date → "YYYY/MM/DD HH:MM"（展示）
function fmtDisplay(d: Date | null | undefined): string {
  if (!d) return '';
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface MonthGridProps {
  year: number;
  month: number;
  selected: Date | null;
  onClick: (date: Date) => void;
}

function MonthGrid({ year, month, selected, onClick }: MonthGridProps) {
  const today = new Date();
  const first = new Date(year, month, 1);
  const startDow = (first.getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonthDays = new Date(year, month, 0).getDate();

  const cells: { date: Date; outside: boolean }[] = [];
  for (let i = startDow; i > 0; i--)
    cells.push({ date: new Date(year, month - 1, prevMonthDays - i + 1), outside: true });
  for (let d = 1; d <= daysInMonth; d++)
    cells.push({ date: new Date(year, month, d), outside: false });
  let next = 1;
  while (cells.length < 42) cells.push({ date: new Date(year, month + 1, next++), outside: true });

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          padding: '2px 0 6px',
          borderBottom: '1px solid var(--border)',
        }}
      >
        {DOW.map((d, i) => (
          <div key={i} style={{ textAlign: 'center', fontSize: 11, color: 'var(--fg-muted)' }}>
            {d}
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginTop: 2 }}>
        {cells.map((c, i) => {
          const isSel = selected && sameDay(c.date, selected);
          const isToday = sameDay(c.date, today);
          return (
            <div
              key={i}
              onClick={() => !c.outside && onClick(c.date)}
              style={{
                height: 32,
                position: 'relative',
                cursor: c.outside ? 'default' : 'pointer',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 2,
                  borderRadius: 999,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12.5,
                  fontFamily: 'var(--font-display)',
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: isSel ? 600 : isToday ? 600 : 400,
                  color: isSel ? 'var(--accent-fg)' : c.outside ? 'var(--fg-faint)' : 'var(--fg)',
                  background: isSel ? 'var(--accent)' : 'transparent',
                  transition: 'background 0.08s',
                }}
                onMouseEnter={(e) => {
                  if (!c.outside && !isSel) e.currentTarget.style.background = 'var(--bg-muted)';
                }}
                onMouseLeave={(e) => {
                  if (!isSel) e.currentTarget.style.background = 'transparent';
                }}
              >
                {c.date.getDate()}
              </div>
              {isToday && !isSel && (
                <span
                  style={{
                    position: 'absolute',
                    bottom: 2,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 4,
                    height: 4,
                    borderRadius: 999,
                    background: 'var(--accent)',
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// 快捷时间预设 [时, 分]
const TIME_PRESETS: [number, number][] = [
  [0, 0],
  [8, 0],
  [12, 0],
  [18, 0],
  [20, 0],
  [22, 0],
];

export interface DateTimePanelProps {
  initial: Date | null;
  onApply: (d: Date) => void;
  onCancel: () => void;
}

export function DateTimePanel({ initial, onApply, onCancel }: DateTimePanelProps) {
  const [sel, setSel] = useState<Date | null>(
    initial ? new Date(initial.getFullYear(), initial.getMonth(), initial.getDate()) : null,
  );
  const [hour, setHour] = useState(initial ? initial.getHours() : 0);
  const [minute, setMinute] = useState(initial ? initial.getMinutes() : 0);
  const init = initial || new Date();
  const [viewY, setViewY] = useState(init.getFullYear());
  const [viewM, setViewM] = useState(init.getMonth());

  const stepMonth = (delta: number) => {
    const d = new Date(viewY, viewM + delta, 1);
    setViewM(d.getMonth());
    setViewY(d.getFullYear());
  };

  const apply = () => {
    if (!sel) return;
    onApply(new Date(sel.getFullYear(), sel.getMonth(), sel.getDate(), hour, minute));
  };

  return (
    <div
      style={{
        background: 'var(--bg-elev)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        boxShadow: 'var(--shadow-lg)',
        width: 300,
        overflow: 'hidden',
        animation: 'fade-in 0.12s ease',
      }}
    >
      <div style={{ padding: 14 }}>
        {/* 月份导航 */}
        <div className="hstack" style={{ marginBottom: 6 }}>
          <button
            className="btn btn-ghost btn-icon"
            onClick={() => stepMonth(-1)}
            title="上个月"
            style={{ width: 26, height: 26 }}
          >
            <Icons.IconChevLeft size={13} />
          </button>
          <div
            style={{
              flex: 1,
              textAlign: 'center',
              fontSize: 13.5,
              fontWeight: 500,
              color: 'var(--fg-strong)',
              letterSpacing: '-0.005em',
            }}
          >
            {viewY}年 {MONTHS[viewM]}
          </div>
          <button
            className="btn btn-ghost btn-icon"
            onClick={() => stepMonth(1)}
            title="下个月"
            style={{ width: 26, height: 26 }}
          >
            <Icons.IconChevRight size={13} />
          </button>
        </div>

        <MonthGrid year={viewY} month={viewM} selected={sel} onClick={setSel} />

        {/* 时/分步进 */}
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
          <div
            className="hstack"
            style={{ gap: 8, alignItems: 'center', justifyContent: 'center' }}
          >
            <div
              className="hstack"
              style={{
                gap: 0,
                border: '1px solid var(--border)',
                borderRadius: 7,
                overflow: 'hidden',
              }}
            >
              <button
                className="btn btn-ghost btn-icon"
                style={{ width: 28, height: 32, borderRadius: 0 }}
                onClick={() => setHour((h) => (h + 23) % 24)}
              >
                <Icons.IconChevLeft size={12} />
              </button>
              <div
                className="mono"
                style={{ width: 40, textAlign: 'center', fontSize: 18, fontWeight: 600 }}
              >
                {pad(hour)}
              </div>
              <button
                className="btn btn-ghost btn-icon"
                style={{ width: 28, height: 32, borderRadius: 0 }}
                onClick={() => setHour((h) => (h + 1) % 24)}
              >
                <Icons.IconChevRight size={12} />
              </button>
            </div>
            <span
              className="mono"
              style={{ fontSize: 18, fontWeight: 600, color: 'var(--fg-muted)' }}
            >
              :
            </span>
            <div
              className="hstack"
              style={{
                gap: 0,
                border: '1px solid var(--border)',
                borderRadius: 7,
                overflow: 'hidden',
              }}
            >
              <button
                className="btn btn-ghost btn-icon"
                style={{ width: 28, height: 32, borderRadius: 0 }}
                onClick={() => setMinute((m) => (m + 55) % 60)}
              >
                <Icons.IconChevLeft size={12} />
              </button>
              <div
                className="mono"
                style={{ width: 40, textAlign: 'center', fontSize: 18, fontWeight: 600 }}
              >
                {pad(minute)}
              </div>
              <button
                className="btn btn-ghost btn-icon"
                style={{ width: 28, height: 32, borderRadius: 0 }}
                onClick={() => setMinute((m) => (m + 5) % 60)}
              >
                <Icons.IconChevRight size={12} />
              </button>
            </div>
            <span
              className="mono muted"
              style={{ fontSize: 10, marginLeft: 2, color: 'var(--fg-muted)' }}
            >
              步进 5
            </span>
          </div>
          {/* 快捷时间预设 */}
          <div
            className="hstack"
            style={{ gap: 6, marginTop: 8, flexWrap: 'wrap', justifyContent: 'center' }}
          >
            {TIME_PRESETS.map(([h, m]) => {
              const active = hour === h && minute === m;
              return (
                <button
                  key={`${h}:${m}`}
                  onClick={() => {
                    setHour(h);
                    setMinute(m);
                  }}
                  className="mono"
                  style={{
                    padding: '2px 9px',
                    borderRadius: 999,
                    font: 'inherit',
                    fontSize: 11,
                    cursor: 'pointer',
                    border: active ? '1px solid var(--accent)' : '1px solid var(--border)',
                    background: active ? 'var(--accent-soft)' : 'var(--bg-elev)',
                    color: active ? 'var(--accent-fg)' : 'var(--fg-muted)',
                  }}
                >
                  {pad(h)}:{pad(m)}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* 页脚：实时预览 + 应用 */}
      <div
        className="hstack"
        style={{
          padding: '10px 14px',
          borderTop: '1px solid var(--border)',
          gap: 8,
          background: 'var(--bg-subtle)',
        }}
      >
        <span
          className="mono"
          style={{ fontSize: 11.5, color: sel ? 'var(--fg)' : 'var(--fg-faint)' }}
        >
          {sel
            ? `${sel.getFullYear()}/${pad(sel.getMonth() + 1)}/${pad(sel.getDate())} ${pad(hour)}:${pad(minute)}`
            : '未选择日期'}
        </span>
        <div style={{ flex: 1 }} />
        <button className="btn btn-sm" onClick={onCancel}>
          取消
        </button>
        <button className="btn btn-sm btn-accent" disabled={!sel} onClick={apply}>
          <Icons.IconCheck size={11} />
          应用
        </button>
      </div>
    </div>
  );
}

export interface DateTimeButtonProps {
  value?: string;
  onChange: (v: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  buttonClass?: string;
  buttonStyle?: React.CSSProperties;
  disabled?: boolean;
}

export function DateTimeButton({
  value,
  onChange,
  placeholder = '选择日期时间',
  style,
  buttonClass = 'input mono',
  buttonStyle,
  disabled,
}: DateTimeButtonProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const ownedPortalRef = useOwnedFocusScopePortalRef<HTMLDivElement>(triggerRef, undefined, true);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const cur = parseDT(value);

  useEffect(() => {
    if (!open) return;
    const update = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      const w = 300;
      const margin = 8;
      const h = 420;
      let left = r.left;
      if (left + w > window.innerWidth - margin) left = window.innerWidth - w - margin;
      if (left < margin) left = margin;
      let top = r.bottom + 6;
      if (top + h > window.innerHeight - margin) top = Math.max(margin, r.top - h - 6);
      setPos({ top, left });
    };
    update();
    const onClick = (e: MouseEvent) => {
      const dlg = document.getElementById('datetime-portal');
      const target = e.target as Node;
      if (dlg && !dlg.contains(target) && !triggerRef.current?.contains(target)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const apply = (d: Date) => {
    setOpen(false);
    onChange(fmtDT(d));
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={buttonClass}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          cursor: disabled ? 'not-allowed' : 'pointer',
          textAlign: 'left',
          ...buttonStyle,
          ...style,
        }}
      >
        <Icons.IconCalendar size={12} style={{ color: 'var(--fg-muted)', flexShrink: 0 }} />
        <span style={{ flex: 1, color: cur ? 'var(--fg)' : 'var(--fg-faint)' }}>
          {cur ? fmtDisplay(cur) : placeholder}
        </span>
        <Icons.IconChevDown size={11} style={{ color: 'var(--fg-muted)', flexShrink: 0 }} />
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={ownedPortalRef}
            id="datetime-portal"
            style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 200 }}
          >
            <DateTimePanel initial={cur} onApply={apply} onCancel={() => setOpen(false)} />
          </div>,
          document.body,
        )}
    </>
  );
}
